/* eslint-disable react/forbid-prop-types */
/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import './style.scss';

import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import PropTypes from 'prop-types';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { useState } from 'react';
import { submitFinancialAdjustment } from 'apis/financialAdjustment.api';
import { toast } from 'layout';
import { TOAST } from 'constant';
import Form from './Form';
import GeneralInfo from './GeneralInfo';
import ReplyAdjustmentCommentModal from '../ReplyAdjustmentCommentModal';

function FinancialAdjustmentForm({
  control,
  errors,
  handleSubmit,
  refetch,
  checkKeyDown,
  footerConfirmationPrompt,
  readOnly,
  isDirty,
  dirtyFields,
  watch,
  setValue,
  project,
  financialAdjustmentId,
  lastComment,
}) {
  // #region Data
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isRequestedEdit } = location.state || {};

  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmitReviseModalOpen, setIsSubmitReviseModalOpen] = useState(false);

  const { mutateAsync, isLoading } = useMutation(submitFinancialAdjustment);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmitFinancialAdjustment = async () => {
    try {
      await mutateAsync(financialAdjustmentId);
      if (isRequestedEdit) {
        toast(TOAST.SUCCESS, t('financialAdjustment.notification.submitRevise'));
        setIsSubmitReviseModalOpen(false);
      } else {
        toast(TOAST.SUCCESS, t('financialAdjustment.notification.submit'));
        setIsSubmitModalOpen(false);
      }
      refetch();
      setIsSubmitted(true);
    } catch (error) {
      toast(TOAST.ERROR, t('financialAdjustment.error'));
    }
  };

  const footerConfirmationSubmit = () => (
    <div>
      <Button
        label={t('projectProposal.cancel')}
        className="w-7rem"
        severity="danger"
        onClick={() => setIsSubmitModalOpen(false)}
        autoFocus
      />
      <Button
        label={t('projectProposal.ok')}
        className="w-7rem"
        severity="info"
        onClick={() => {
          handleSubmitFinancialAdjustment();
        }}
      />
    </div>
  );

  return (
    <div className="bg-white p-5 financial-adjustment">
      <Dialog
        header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
        visible={isSubmitModalOpen}
        position="center"
        style={{ width: '400px' }}
        onHide={() => setIsSubmitModalOpen(false)}
        draggable={false}
        footer={footerConfirmationSubmit}
      >
        <Message
          className="mt-4 w-full"
          severity="warn"
          text={t('financialAdjustment.confirmationMessage')}
        />
      </Dialog>

      <GeneralInfo
        data={project}
        financialAdjustmentId={financialAdjustmentId}
        lastComment={lastComment}
      />
      <form onSubmit={handleSubmit} onKeyDown={checkKeyDown} autoComplete="off">
        <Form
          errors={errors}
          control={control}
          footerConfirmationPrompt={footerConfirmationPrompt}
          isDirty={isDirty}
          dirtyFields={dirtyFields}
          watch={watch}
          setValue={setValue}
          readOnly={readOnly || isSubmitted}
        />

        <div className="flex justify-content-end mt-2 gap-4">
          <Button
            className="w-10rem"
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
            label={t('contract.comeBack')}
            severity="danger"
            type="button"
            onClick={() => {
              navigate(-1);
            }}
          />
          {!readOnly && (
            <>
              <Button
                type="submit"
                disabled={
                  financialAdjustmentId
                    ? !(isDirty && Object.keys(dirtyFields).length)
                    : !dirtyFields?.commitment || !dirtyFields?.financialAdjustmentItems?.length > 0
                }
                className=" w-10rem "
                style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                {t('contract.save')}
              </Button>
              {!isRequestedEdit ? (
                <Button
                  type="button"
                  className="btn-control w-11rem"
                  icon={`pi mr-1 ${isLoading ? 'pi-spin pi-spinner' : 'pi pi-check'}`}
                  disabled={
                    !financialAdjustmentId ||
                    isSubmitted ||
                    (isDirty && Object.keys(dirtyFields).length)
                  }
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  severity="success"
                  onClick={() => {
                    setIsSubmitModalOpen(true);
                  }}
                >
                  {t('financialAdjustment.submit')}
                </Button>
              ) : (
                <Button
                  type="button"
                  className="btn-control w-14rem"
                  icon={`pi mr-1 ${isLoading ? 'pi-spin pi-spinner' : 'pi pi-check'}`}
                  disabled={
                    !financialAdjustmentId ||
                    isSubmitted ||
                    (isDirty && Object.keys(dirtyFields).length)
                  }
                  style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  severity="warning"
                  onClick={() => {
                    setIsSubmitReviseModalOpen(true);
                  }}
                >
                  {t('extendImplementationTime.revise')}
                </Button>
              )}
            </>
          )}
        </div>
      </form>
      <ReplyAdjustmentCommentModal
        visible={isSubmitReviseModalOpen}
        onClose={() => setIsSubmitReviseModalOpen(false)}
        adjustmentCommentId={lastComment?.id}
        onSubmit={handleSubmitFinancialAdjustment}
        isSubmitting={isLoading}
        isRevise
      />
    </div>
  );
}

FinancialAdjustmentForm.propTypes = {
  control: PropTypes.shape({}).isRequired,
  errors: PropTypes.shape({}).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  checkKeyDown: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  footerConfirmationPrompt: PropTypes.func,
  refetch: PropTypes.func.isRequired,
  dirtyFields: PropTypes.object,
  isDirty: PropTypes.bool,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  project: PropTypes.shape({}).isRequired,
  financialAdjustmentId: PropTypes.string,
  lastComment: PropTypes.object,
};

FinancialAdjustmentForm.defaultProps = {
  readOnly: false,
  dirtyFields: {},
  isDirty: false,
  footerConfirmationPrompt: () => null,
  financialAdjustmentId: '',
  lastComment: null,
};

export default FinancialAdjustmentForm;
